import React from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Text,
} from "@tremor/react";
import { Link } from "react-router-dom";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { toFriendlyTime } from "../services/utils";

/**
 * Get status color and indicator based on last seen time and device type
 */
function getDeviceStatus(lastOnline, deviceType) {
  if (!lastOnline) {
    return { color: 'gray', text: 'Never' };
  }
  
  const now = new Date();
  const lastSeen = new Date(lastOnline);
  const minutesSince = (now - lastSeen) / (1000 * 60);
  
  // Different thresholds for LoRa vs MQTT devices
  if (deviceType === 'lora') {
    // LoRa devices: Online < 60min, Offline >= 60min
    if (minutesSince < 60) {
      return { color: 'green', text: 'Online' };
    } else {
      return { color: 'red', text: 'Offline' };
    }
  } else {
    // MQTT devices: Online < 5min, Recent < 60min, Offline >= 60min
    if (minutesSince < 5) {
      return { color: 'green', text: 'Online' };
    } else if (minutesSince < 60) {
      return { color: 'yellow', text: 'Recent' };
    } else {
      return { color: 'red', text: 'Offline' };
    }
  }
}

/**
 * Map capability type/name to a human-readable label
 */
const readableLabels = {
  temperature: 'Temperature',
  humidity: 'Humidity',
  rel_hum: 'Humidity',
  pressure: 'Pressure',
  battery: 'Battery',
  voltage: 'Battery',
  current: 'Current',
  power: 'Power',
  speed: 'Speed',
  distance: 'Distance',
  acceleration: 'Acceleration',
  gyroscope: 'Gyroscope',
  magnetometer: 'Compass',
  light: 'Light',
  lux: 'Light',
  sound: 'Sound',
  motion: 'Motion',
  door: 'Door',
  window: 'Window',
  switch: 'Switch',
  button: 'Button',
  weight: 'Weight',
  level: 'Level',
  ph: 'pH',
  co2: 'CO2',
  rssi: 'Signal Strength',
  snr: 'SNR',
  per: 'Percentage',
  temp: 'Temperature',
  batt: 'Battery',
};

function getReadableLabel(capability) {
  const key = capability.type?.toLowerCase() || capability.name?.toLowerCase();
  return readableLabels[key] || capability.name || capability.type || 'Sensor';
}

/**
 * Get structured label + value for a capability
 */
function getCapabilityDisplay(capability) {
  const { value, unit, type, array_value } = capability;

  if (type === 'gps' && array_value && array_value.length >= 2) {
    const [lat, lon] = array_value;
    return { label: 'Location', value: `${lat.toFixed(3)},${lon.toFixed(3)}` };
  }

  const label = getReadableLabel(capability);

  if (value !== null && value !== undefined) {
    const formattedValue = typeof value === 'number' ?
      (value % 1 === 0 ? value : value.toFixed(1)) :
      value;
    return { label, value: `${formattedValue}${unit || ''}` };
  }

  return { label, value: '--' };
}

function DeviceRow({ device }) {
  const [expanded, setExpanded] = React.useState(false);
  const status = getDeviceStatus(device.lastOnline, device.type);
  const capabilitiesToShow = expanded
    ? device.capabilities
    : (device.capabilities || []).slice(0, 4);
  const hasMore = device.capabilities && device.capabilities.length > 4;

  return (
    <TableRow className="hover:bg-gray-50 dark:hover:bg-gray-800">
      {/* Device Info */}
      <TableCell className="py-4">
        <Link
          to={`/devices/${device._id}`}
          className="block hover:underline hover:underline-offset-4"
        >
          <div className="flex flex-col">
            <span className="font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
              {device.name}
            </span>
            <span className="text-sm text-tremor-content-subtle dark:text-dark-tremor-content-subtle">
              {device.serial}
            </span>
            <Badge
              className="mt-1 w-fit"
              size="xs"
              color={device.type === 'mqtt' ? 'blue' : 'purple'}
            >
              {device.type === 'mqtt' ? 'MQTT' : 'LoRaWAN'}
            </Badge>
          </div>
        </Link>
      </TableCell>

      {/* Status */}
      <TableCell className="py-4">
        <div className="flex flex-col gap-1">
          <Badge size="xs" color={status.color}>
            {status.text}
          </Badge>
          <Text className="text-xs text-tremor-content-subtle dark:text-dark-tremor-content-subtle">
            {device.lastOnline ? toFriendlyTime(device.lastOnline) : 'Never seen'}
          </Text>
        </div>
      </TableCell>

      {/* Latest Values */}
      <TableCell className="py-4">
        <div className="flex flex-wrap gap-1 sm:gap-2">
          {capabilitiesToShow.length > 0 ? (
            capabilitiesToShow.map((capability, index) => {
              const display = getCapabilityDisplay(capability);
              return (
                <div
                  key={`${capability.channel}-${index}`}
                  className="flex flex-col rounded-tremor-small border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5"
                >
                  <span className="text-[10px] text-tremor-content-subtle dark:text-dark-tremor-content-subtle leading-tight">{display.label}</span>
                  <span className="text-sm font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong leading-tight">{display.value}</span>
                </div>
              );
            })
          ) : (
            <Text className="text-sm text-tremor-content-subtle dark:text-dark-tremor-content-subtle italic">
              No data
            </Text>
          )}
          {hasMore && !expanded && (
            <button
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-0.5 rounded-tremor-small border border-gray-300 dark:border-gray-800 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-tremor-content dark:text-dark-tremor-content hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
            >
              +{device.capabilities.length - 4} more
              <ChevronDownIcon className="h-3 w-3" />
            </button>
          )}
          {hasMore && expanded && (
            <button
              onClick={() => setExpanded(false)}
              className="inline-flex items-center gap-0.5 rounded-tremor-small border border-gray-300 dark:border-gray-800 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-tremor-content dark:text-dark-tremor-content hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
            >
              Show less
              <ChevronUpIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function DeviceDataTable({ devices }) {
  return (
    <div className="overflow-x-auto">
      <Table className="min-w-full">
        <TableHead>
          <TableRow className="border-b border-tremor-border dark:border-dark-tremor-border">
            <TableHeaderCell className="text-left min-w-[200px]">
              Device
            </TableHeaderCell>
            <TableHeaderCell className="text-left min-w-[100px]">
              Status
            </TableHeaderCell>
            <TableHeaderCell className="text-left min-w-[300px]">
              Latest Values
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device) => (
            <DeviceRow key={device._id} device={device} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}